import React, { useState, useEffect, useCallback } from 'react';
import { Plus, CheckCircle, XCircle, RefreshCw, Wrench, TrendingDown, Upload, Download } from 'lucide-react';
import api, { mastersApi } from '../services/api';
import { Table, Pagination, Modal, Field, Spinner, Tabs, Alert } from '../components/common';
import AssetSearchInput from '../components/common/AssetSearchInput';
import BulkUploadModal from '../components/common/BulkUploadModal';
import { fmt, StatusBadge, useDebounce } from '../utils/helpers';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const txApi2 = {
  transfers:      p  => api.get('/transactions/transfers', { params: p }).then(r => r.data),
  createTransfer: d  => api.post('/transactions/transfers', d).then(r => r.data),
  actionTransfer: (id, action, remarks) => api.patch(`/transactions/transfers/${id}/action`, { action, remarks }).then(r => r.data),
  maintenance:    p  => api.get('/transactions/maintenance', { params: p }).then(r => r.data),
  createMaint:    d  => api.post('/transactions/maintenance', d).then(r => r.data),
  depPeriods:     () => api.get('/depreciation/schedule').then(r => r.data),
  runDep:         d  => api.post('/depreciation/run', d).then(r => r.data),
};

// ─── Transfers ────────────────────────────────────────────
export function Transfers() {
  const { can } = useAuth();
  const [data,    setData]    = useState({ data: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [tab,     setTab]     = useState('all');
  const [showCreate, setCreate] = useState(false);
  const [showBulk, setBulk]   = useState(false);
  const canApprove = can('transactions:transfers:approve');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await txApi2.transfers({ page, limit: 25, status: tab === 'all' ? undefined : tab });
      setData(r);
    } catch {} finally { setLoading(false); }
  }, [page, tab]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    const remarks = action === 'rejected' ? window.prompt('Rejection reason (optional):') || '' : '';
    try {
      await txApi2.actionTransfer(id, action, remarks);
      toast.success(`Transfer ${action}`);
      load();
    } catch {}
  };

  const cols = [
    { key: 'transfer_no',    title: 'Transfer No',  render: v => <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 'var(--fs-xs)' }}>{v}</span> },
    { key: 'asset_code',     title: 'Asset',        render: (v, r) => <><span style={{ fontWeight: 500, fontSize: 'var(--fs-sm)' }}>{v}</span><br/><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmt.truncate(r.asset_name, 28)}</span></> },
    { key: 'from_location',  title: 'From',         render: v => <span style={{ fontSize: 'var(--fs-xs)' }}>{v || '—'}</span> },
    { key: 'to_location',    title: 'To',           render: v => <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--accent)', fontWeight: 500 }}>{v || '—'}</span> },
    { key: 'transfer_date',  title: 'Date',         render: v => fmt.date(v) },
    { key: 'requested_by_name', title: 'By',        render: v => <span style={{ fontSize: 'var(--fs-xs)' }}>{v || '—'}</span> },
    { key: 'status',         title: 'Status',       render: v => <StatusBadge status={v} /> },
    { key: 'id', title: '', align: 'right', render: (_, r) => r.status === 'pending' && canApprove && (
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn-success btn-sm btn-icon" onClick={() => handleAction(r.id, 'approved')} title="Approve"><CheckCircle size={13}/></button>
        <button className="btn btn-danger btn-sm btn-icon"  onClick={() => handleAction(r.id, 'rejected')} title="Reject"><XCircle size={13}/></button>
      </div>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Asset Transfers</h1><p className="page-sub">Inter-location and inter-department asset movements</p></div>
        {can('transactions:transfers:create') && (
          <div className="page-actions">
            <button className="btn btn-secondary" onClick={() => setBulk(true)}><Upload size={13}/> Bulk Upload</button>
            <button className="btn btn-primary"   onClick={() => setCreate(true)}><Plus size={14}/> New Transfer</button>
          </div>
        )}
      </div>
      <div className="tabs">
        {[{ k: 'all', l: 'All' }, { k: 'pending', l: 'Pending Approval' }, { k: 'approved', l: 'Approved' }, { k: 'rejected', l: 'Rejected' }].map(t => (
          <button key={t.k} className={`tab-btn${tab === t.k ? ' active' : ''}`} onClick={() => { setTab(t.k); setPage(1); }}>{t.l}</button>
        ))}
      </div>
      <div className="card">
        <Table columns={cols} data={data.data} loading={loading} emptyText="No transfer records found" />
        {data.pagination?.total > 0 && <Pagination page={page} totalPages={data.pagination.totalPages || 1} total={data.pagination.total} limit={25} onPage={setPage} />}
      </div>
      <TransferModal open={showCreate} onClose={() => setCreate(false)} onSuccess={() => { setCreate(false); load(); }} />
      <BulkUploadModal open={showBulk} onClose={() => setBulk(false)} onSuccess={load} type="transfers" title="Asset Transfers" />
    </div>
  );
}

function TransferModal({ open, onClose, onSuccess }) {
  const [form,      setForm]     = useState({ transfer_date: new Date().toISOString().split('T')[0] });
  const [loading,   setLoading]  = useState(false);
  const [locations, setLocs]     = useState([]);
  const [departments, setDepts]  = useState([]);
  const [custodians, setCusts]   = useState([]);
  const [assetInfo, setAssetInfo]= useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    mastersApi.locations({ limit: 500 }).then(r => setLocs(r.data || []));
    mastersApi.departments({ limit: 300 }).then(r => setDepts(r.data || []));
    mastersApi.custodians({ limit: 500 }).then(r => setCusts(r.data || []));
  }, [open]);

  const handleAssetSelect = (assetId, asset) => {
    setForm(f => ({ ...f, asset_id: assetId }));
    setAssetInfo(asset);
  };

  const save = async () => {
    if (!form.asset_id)            { toast.error('Select an asset'); return; }
    if (!form.to_location_id)      { toast.error('Destination location required'); return; }
    setLoading(true);
    try {
      await txApi2.createTransfer(form);
      toast.success('Transfer request submitted');
      setForm({ transfer_date: new Date().toISOString().split('T')[0] });
      setAssetInfo(null);
      onSuccess();
    } catch (err) {
      // The server's message is already shown by the API error handler (services/api.js).
    } finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="New Transfer Request" size="md"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <Spinner size="sm"/> : 'Submit Transfer'}</button></>}>

      <Field label="Asset" required>
        <AssetSearchInput value={form.asset_id || ''} onChange={handleAssetSelect} placeholder="Search by asset code or name…" />
      </Field>

      {assetInfo && (
        <div style={{ background: 'var(--bg)', borderRadius: 'var(--r-md)', padding: '10px 14px', marginBottom: 16, fontSize: 'var(--fs-xs)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
          {[['Current Location', assetInfo.location_name], ['Current Dept', assetInfo.department_name], ['Custodian', assetInfo.custodian_name], ['Status', assetInfo.asset_status]].map(([l, v]) => (
            <div key={l}><span style={{ color: 'var(--text-muted)' }}>{l}: </span><span style={{ fontWeight: 500 }}>{v || '—'}</span></div>
          ))}
        </div>
      )}

      <Field label="Destination Location" required>
        <select className="form-control" value={form.to_location_id || ''} onChange={set('to_location_id')}>
          <option value="">Select location…</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </Field>

      <div className="form-row cols-2">
        <Field label="Destination Department">
          <select className="form-control" value={form.to_department_id || ''} onChange={set('to_department_id')}>
            <option value="">Same department</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Field>
        <Field label="New Custodian">
          <select className="form-control" value={form.to_custodian_id || ''} onChange={set('to_custodian_id')}>
            <option value="">Same custodian</option>
            {custodians.map(c => <option key={c.id} value={c.id}>{c.full_name} {c.employee_id ? `(${c.employee_id})` : ''}</option>)}
          </select>
        </Field>
      </div>

      <div className="form-row cols-2">
        <Field label="Transfer Date">
          <input className="form-control" type="date" value={form.transfer_date || ''} onChange={set('transfer_date')} />
        </Field>
      </div>

      <Field label="Reason">
        <textarea className="form-control" rows={2} value={form.reason || ''} onChange={set('reason')} placeholder="Reason for transfer…" />
      </Field>
    </Modal>
  );
}

// ─── Maintenance ─────────────────────────────────────────
export function Maintenance() {
  const [data,    setData]   = useState({ data: [], pagination: {} });
  const [loading, setLoading]= useState(true);
  const [page,    setPage]   = useState(1);
  const [tab,     setTab]    = useState('all');
  const [showCreate, setCreate] = useState(false);
  const [showBulk,   setBulk]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await txApi2.maintenance({ page, limit: 25, ...(tab !== 'all' ? { type: tab } : {}) });
      setData(r);
    } catch {} finally { setLoading(false); }
  }, [page, tab]);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key: 'maintenance_no', title: 'No.', render: v => <span style={{ fontFamily: 'monospace', fontSize: 'var(--fs-xs)', fontWeight: 600 }}>{v}</span> },
    { key: 'asset_code',     title: 'Asset', render: (v, r) => <><span style={{ fontWeight: 500 }}>{v}</span><br/><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmt.truncate(r.asset_name, 28)}</span></> },
    { key: 'maintenance_type', title: 'Type', render: v => <span className={`badge ${v === 'amc' ? 'badge-blue' : v === 'preventive' ? 'badge-teal' : 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{v}</span> },
    { key: 'vendor_name',    title: 'Vendor',   render: v => <span style={{ fontSize: 'var(--fs-xs)' }}>{v || '—'}</span> },
    { key: 'service_date',   title: 'Service',  render: v => fmt.date(v) },
    { key: 'next_service_date', title: 'Next Due', render: v => <span style={{ fontSize: 'var(--fs-xs)', color: v && new Date(v) < new Date() ? 'var(--danger)' : 'var(--text-secondary)' }}>{fmt.date(v)}</span> },
    { key: 'cost',           title: 'Cost (₹)', align: 'right', render: v => <span style={{ fontFamily: 'monospace', fontSize: 'var(--fs-xs)' }}>{fmt.currency(v)}</span> },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Asset Maintenance</h1><p className="page-sub">Service history, AMC tracking, upcoming maintenance</p></div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={() => setBulk(true)}><Upload size={13}/> Bulk Upload</button>
          <button className="btn btn-primary"   onClick={() => setCreate(true)}><Plus size={14}/> Log Maintenance</button>
        </div>
      </div>
      <div className="tabs">
        {[{ k:'all',l:'All'},{k:'corrective',l:'Corrective'},{k:'preventive',l:'Preventive'},{k:'amc',l:'AMC'}].map(t => (
          <button key={t.k} className={`tab-btn${tab === t.k ? ' active' : ''}`} onClick={() => { setTab(t.k); setPage(1); }}>{t.l}</button>
        ))}
      </div>
      <div className="card">
        <Table columns={cols} data={data.data} loading={loading} emptyText="No maintenance records" />
        {data.pagination?.total > 0 && <Pagination page={page} totalPages={data.pagination.totalPages || 1} total={data.pagination.total} limit={25} onPage={setPage} />}
      </div>
      <MaintenanceModal open={showCreate} onClose={() => setCreate(false)} onSuccess={() => { setCreate(false); load(); }} />
      <BulkUploadModal open={showBulk} onClose={() => setBulk(false)} onSuccess={load} type="maintenance" title="Asset Maintenance" />
    </div>
  );
}

function MaintenanceModal({ open, onClose, onSuccess }) {
  const [form,    setForm]   = useState({ maintenance_type: 'corrective', service_date: new Date().toISOString().split('T')[0] });
  const [loading, setLoading]= useState(false);
  const [vendors, setVendors]= useState([]);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    mastersApi.vendors({ limit: 500 }).then(r => setVendors(r.data || []));
  }, [open]);

  const save = async () => {
    if (!form.asset_id) { toast.error('Select an asset'); return; }
    if (!form.service_date) { toast.error('Service date required'); return; }
    setLoading(true);
    try {
      await txApi2.createMaint(form);
      toast.success('Maintenance record saved');
      setForm({ maintenance_type: 'corrective', service_date: new Date().toISOString().split('T')[0] });
      onSuccess();
    } catch (err) {
      // The server's message is already shown by the API error handler (services/api.js).
    } finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Log Maintenance / AMC" size="md"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <Spinner size="sm"/> : 'Save'}</button></>}>
      <Field label="Asset" required>
        <AssetSearchInput value={form.asset_id || ''} onChange={(id) => setForm(f => ({ ...f, asset_id: id }))} />
      </Field>
      <div className="form-row cols-2">
        <Field label="Maintenance Type">
          <select className="form-control" value={form.maintenance_type} onChange={set('maintenance_type')}>
            <option value="corrective">Corrective (Breakdown)</option>
            <option value="preventive">Preventive (Scheduled)</option>
            <option value="amc">AMC</option>
            <option value="calibration">Calibration</option>
          </select>
        </Field>
        <Field label="Vendor">
          <select className="form-control" value={form.vendor_id || ''} onChange={set('vendor_id')}>
            <option value="">Select vendor…</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
      </div>
      <div className="form-row cols-2">
        <Field label="Service Date" required><input className="form-control" type="date" value={form.service_date || ''} onChange={set('service_date')} /></Field>
        <Field label="Next Service Date"><input className="form-control" type="date" value={form.next_service_date || ''} onChange={set('next_service_date')} /></Field>
      </div>
      <div className="form-row cols-2">
        <Field label="Cost (₹)"><input className="form-control" type="number" value={form.cost || ''} onChange={set('cost')} placeholder="0" /></Field>
        <Field label="Technician Name"><input className="form-control" value={form.technician_name || ''} onChange={set('technician_name')} /></Field>
      </div>
      {form.maintenance_type === 'amc' && (
        <div className="form-row cols-2">
          <Field label="AMC Contract No"><input className="form-control" value={form.amc_contract_no || ''} onChange={set('amc_contract_no')} /></Field>
          <Field label="AMC End Date"><input className="form-control" type="date" value={form.amc_end_date || ''} onChange={set('amc_end_date')} /></Field>
        </div>
      )}
      <Field label="Description"><textarea className="form-control" rows={2} value={form.description || ''} onChange={set('description')} /></Field>
    </Modal>
  );
}

// ─── Depreciation ─────────────────────────────────────────
export function DepreciationPage() {
  const [periods, setPeriods]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [running, setRunning]   = useState(false);
  const [form,    setForm]      = useState({ dep_method: 'both', period_type: 'monthly' });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    txApi2.depPeriods().then(r => { setPeriods(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleRun = async () => {
    if (!form.period_start || !form.period_end) { toast.error('Select period start and end dates'); return; }
    if (!window.confirm('Run depreciation for this period? This will create depreciation entries for all active assets.')) return;
    setRunning(true);
    try {
      const r = await txApi2.runDep(form);
      toast.success(`Depreciation completed — ${r.data?.assets_processed || 0} assets processed`);
      txApi2.depPeriods().then(rr => setPeriods(rr.data || []));
    } catch (err) {
      // The server's message is already shown by the API error handler (services/api.js).
    } finally { setRunning(false); }
  };

  const cols = [
    { key: 'period_name',   title: 'Period',    render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'period_type',   title: 'Type',      render: v => <span className="badge badge-blue" style={{ textTransform: 'capitalize' }}>{v}</span> },
    { key: 'start_date',    title: 'Start',     render: v => fmt.date(v) },
    { key: 'end_date',      title: 'End',       render: v => fmt.date(v) },
    { key: 'assets_processed', title: 'Assets', align: 'right', render: v => fmt.number(v || 0) },
    { key: 'total_dep_comp',title: 'Dep (CA)',  align: 'right', render: v => <span style={{ fontFamily: 'monospace', fontSize: 'var(--fs-xs)' }}>{fmt.currency(v)}</span> },
    { key: 'total_dep_it',  title: 'Dep (IT)',  align: 'right', render: v => <span style={{ fontFamily: 'monospace', fontSize: 'var(--fs-xs)' }}>{fmt.currency(v)}</span> },
    { key: 'run_status',    title: 'Status',    render: v => <StatusBadge status={v || 'completed'} /> },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Depreciation</h1><p className="page-sub">Companies Act 2013 + Income Tax Act — SLM &amp; WDV in parallel</p></div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><span className="card-title">Run Depreciation</span></div>
        <div className="card-body">
          <Alert type="warning">Verify all asset purchase dates and categories before running. Each run creates a permanent depreciation entry per asset.</Alert>
          <div className="form-row cols-4" style={{ marginTop: 16 }}>
            <Field label="Period Type">
              <select className="form-control" value={form.period_type} onChange={set('period_type')}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            </Field>
            <Field label="Period Start" required><input className="form-control" type="date" value={form.period_start || ''} onChange={set('period_start')} /></Field>
            <Field label="Period End"   required><input className="form-control" type="date" value={form.period_end   || ''} onChange={set('period_end')} /></Field>
            <Field label="Method">
              <select className="form-control" value={form.dep_method} onChange={set('dep_method')}>
                <option value="both">Both SLM + WDV</option>
                <option value="SLM">SLM Only</option>
                <option value="WDV">WDV Only</option>
              </select>
            </Field>
          </div>
          <button className="btn btn-primary" onClick={handleRun} disabled={running}>
            {running ? <><Spinner size="sm"/> Running Depreciation…</> : <><TrendingDown size={14}/> Run Depreciation</>}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Depreciation History</span></div>
        <Table columns={cols} data={periods} loading={loading} emptyText="No depreciation runs yet" />
      </div>
    </div>
  );
}
